/**
 * The Assistant's rule draft, as a card in the chat.
 *
 * Registered through the Assistant's neutral card seam (`assistant-cards.ts`),
 * so the chat draws it without knowing Argus exists. It shows the sentence the
 * server read the rule as and what the Assistant had to assume.
 *
 * Mostly the rule is already stored - a new rule, or a new version of one that
 * is not deployed, is saved as an undeployed draft - and **Open rule** shows
 * it in the Rules room. A change to a *deployed* rule is not stored: a running
 * rule is not edited (decision 28). **Open in editor** carries it into the
 * block editor unsaved, where it can be backtested; saving waits until the
 * person stops the rule (`docs/plans/argus.md` rule 5).
 */

import { html, nothing, type TemplateResult } from 'lit';
import type { ArgusRuleChart, ArgusTreeDocument, PlotSpec } from '../../api/types.ts';
import { registerAssistantCard } from '../assistant/assistant-cards.ts';
import { registerPagePart } from '../assistant/page-context.ts';
import { openSection } from '../../state/open-section.ts';
import { activeSection } from '../../state/store.ts';
import { stripContext } from '../../state/presets.ts';
import { showToast } from '../../ui/toast.ts';
import { specToQuery } from '../assistant/apply-plot.ts';
import { argusEpisode, argusRoom, openArgusEpisode, setArgusRoom, type ArgusRoom } from './argus-lens.ts';
import { rulesRoomOpenRule } from './argus-inbox-store.ts';
import {
  assistantDraft,
  assistantEditorEdit,
  loadRules,
  openEditor,
  rulesStatus,
  setCharts,
} from './argus-rules-store.ts';

export const ARGUS_RULE_DRAFT_CARD = 'argus.rule_draft';

interface DraftPayload {
  rule_id?: string | null;
  /** Stored as a draft by the server. Absent on cards from before it could. */
  saved?: boolean;
  version?: number | null;
  base_version?: number | null;
  base_name?: string | null;
  name?: string;
  description?: string;
  document?: ArgusTreeDocument;
  sentence?: string | null;
  assumptions?: string[];
}

/** Saved versions whose card has already refreshed the rule list. */
const refreshed = new Set<string>();

/** A card for a rule the server just stored: the list, if anyone has it, is behind. */
function noticeSaved(payload: DraftPayload): void {
  const key = `${payload.rule_id}@${payload.version}`;
  if (refreshed.has(key)) return;
  refreshed.add(key);
  if (rulesStatus.value !== 'idle') void loadRules();
}

function openSaved(event: Event, payload: DraftPayload): void {
  if (!payload.rule_id) return;
  const target = event.currentTarget as EventTarget;
  void loadRules().then(() => rulesRoomOpenRule.set(payload.rule_id ?? null));
  argusRoom.set('rules');
  openSection(target, 'argus');
}

function openUnsaved(event: Event, payload: DraftPayload): void {
  if (!payload.document) return;
  assistantDraft.set({
    ruleId: payload.rule_id ?? null,
    name: payload.name ?? '',
    description: payload.description ?? '',
    document: payload.document,
    assumptions: payload.assumptions ?? [],
  });
  argusRoom.set('rules');
  openSection(event.currentTarget as EventTarget, 'argus');
}

export function renderRuleDraftCard(raw: Record<string, unknown>): TemplateResult {
  const payload = raw as DraftPayload;
  const saved = payload.saved === true && !!payload.rule_id;
  if (saved) noticeSaved(payload);
  const change = !!payload.base_version;
  const title = saved
    ? change
      ? `${payload.name ?? ''} · v${payload.version ?? ''}`
      : `New rule · ${payload.name ?? ''}`
    : change || payload.rule_id
      ? `Change to ${payload.base_name ?? 'a rule'}${payload.base_version ? ` · v${payload.base_version}` : ''}`
      : `New rule · ${payload.name ?? ''}`;
  const status = saved
    ? 'draft · saved · not deployed'
    : payload.rule_id
      ? 'not saved · this rule is running · stop it to save this change'
      : 'draft · not saved';
  const assumptions = payload.assumptions ?? [];
  const button = saved
    ? html`<button class="assistant-send" @click=${(e: Event) => openSaved(e, payload)}>Open rule →</button>`
    : payload.document
      ? html`<button class="assistant-send" @click=${(e: Event) => openUnsaved(e, payload)}>
          Open in editor →
        </button>`
      : nothing;
  return html`
    <div class="assistant-plotcard argus-assist-card">
      <div class="assistant-plotcard-head">
        <span class="assistant-plotcard-title">${title}</span>
        ${button}
      </div>
      <span class="assistant-plotcard-type">${status}</span>
      ${payload.sentence ? html`<p class="argus-assist-sentence">${payload.sentence}</p>` : nothing}
      ${assumptions.length
        ? html`<div class="argus-assist-assumed">
            <span class="argus-assist-assumed-label">Assumed</span>
            <ul class="assistant-dashcard-list">
              ${assumptions.map((line) => html`<li>${line}</li>`)}
            </ul>
          </div>`
        : nothing}
    </div>
  `;
}

registerAssistantCard(ARGUS_RULE_DRAFT_CARD, renderRuleDraftCard);

/* ── where the person is, for the Assistant ─────────────── */

// Only while Argus is on screen: elsewhere, "this rule" means nothing here.
registerPagePart('argus', () => {
  if (activeSection.value !== 'argus') return null;
  const editor = openEditor.value;
  return {
    here: true,
    room: argusRoom.value,
    alert_id: argusEpisode.value,
    editor: editor
      ? {
          mode: editor.mode,
          rule_id: editor.ruleId,
          version: editor.version,
          status: editor.status,
          name: editor.name,
          description: editor.description,
          document: editor.document,
          dirty: editor.dirty,
          readonly: editor.readonly,
        }
      : null,
  };
});

/* ── a change to the open editor ─────────────────────────── */

export const ARGUS_EDITOR_EDIT_CARD = 'argus.editor_edit';

interface EditorEditPayload {
  mode?: 'rule' | 'question';
  rule_id?: string | null;
  document?: ArgusTreeDocument;
  name?: string | null;
  description?: string | null;
  sentence?: string | null;
  summary?: string;
}

function arriveEditorEdit(raw: Record<string, unknown>): void {
  const payload = raw as EditorEditPayload;
  if (!payload.document) return;
  assistantEditorEdit.set({
    mode: payload.mode ?? 'rule',
    ruleId: payload.rule_id ?? null,
    document: payload.document,
    name: payload.name ?? null,
    description: payload.description ?? null,
    summary: payload.summary ?? '',
  });
  // The editor takes it as it is set. Nobody took it: that editor was closed,
  // and the card's own button opens the change instead.
  if (assistantEditorEdit.value) assistantEditorEdit.set(null);
}

function reopenEditorEdit(event: Event, payload: EditorEditPayload): void {
  if (!payload.document) return;
  assistantDraft.set({
    ruleId: payload.rule_id ?? null,
    name: payload.name ?? '',
    description: payload.description ?? '',
    document: payload.document,
    assumptions: [],
  });
  setArgusRoom('rules');
  openSection(event.currentTarget as EventTarget, 'argus');
}

function renderEditorEditCard(raw: Record<string, unknown>): TemplateResult {
  const payload = raw as EditorEditPayload;
  const question = payload.mode === 'question';
  // While the editor it was written for is open, the change is already there.
  const editor = openEditor.value;
  const stillOpen =
    !!editor && editor.mode === (payload.mode ?? 'rule') && editor.ruleId === (payload.rule_id ?? null);
  return html`
    <div class="assistant-plotcard argus-assist-card">
      <div class="assistant-plotcard-head">
        <span class="assistant-plotcard-title">${question ? 'Changed the question' : 'Changed the open rule'}</span>
        ${question || stillOpen
          ? nothing
          : html`<button
              class="assistant-send"
              title="If the editor was closed, open this change in it again"
              @click=${(e: Event) => reopenEditorEdit(e, payload)}
            >
              Open in editor →
            </button>`}
      </div>
      <span class="assistant-plotcard-type">in the editor · not saved</span>
      ${payload.summary ? html`<p>${payload.summary}</p>` : nothing}
      ${payload.sentence ? html`<p class="argus-assist-sentence">${payload.sentence}</p>` : nothing}
    </div>
  `;
}

registerAssistantCard(ARGUS_EDITOR_EDIT_CARD, renderEditorEditCard, arriveEditorEdit);

/* ── going somewhere in Argus ────────────────────────────── */

export const ARGUS_OPEN_CARD = 'argus.open';

interface OpenPayload {
  place?: string;
  rule_id?: string;
  alert_id?: number;
  name?: string;
}

const ROOM_NAMES: Record<string, string> = {
  inbox: 'Inbox',
  rules: 'Rules',
  backtest: 'Backtest',
  history: 'History',
};

function go(payload: OpenPayload, host: EventTarget): void {
  const place = payload.place ?? '';
  if (place === 'alert' && typeof payload.alert_id === 'number') {
    openArgusEpisode(payload.alert_id);
  } else if (place === 'rule' && payload.rule_id) {
    const id = payload.rule_id;
    setArgusRoom('rules');
    void loadRules().then(() => rulesRoomOpenRule.set(id));
  } else if (place in ROOM_NAMES) {
    setArgusRoom(place as ArgusRoom);
  } else {
    return;
  }
  openSection(host, 'argus');
}

function renderOpenCard(raw: Record<string, unknown>): TemplateResult {
  const payload = raw as OpenPayload;
  const place = payload.place ?? '';
  const title =
    place === 'alert'
      ? `Alert · ${payload.name ?? payload.alert_id ?? ''}`
      : place === 'rule'
        ? `Rule · ${payload.name ?? ''}`
        : `Argus · ${ROOM_NAMES[place] ?? place}`;
  return html`
    <div class="assistant-plotcard argus-assist-card">
      <div class="assistant-plotcard-head">
        <span class="assistant-plotcard-title">${title}</span>
        <button class="assistant-send" @click=${(e: Event) => go(payload, e.currentTarget as EventTarget)}>Open →</button>
      </div>
    </div>
  `;
}

registerAssistantCard(ARGUS_OPEN_CARD, renderOpenCard, (raw, host) => go(raw as OpenPayload, host));

/* ── what a rule watches, in Explore ─────────────────────── */

export const ARGUS_EXPLORE_CARD = 'argus.explore';

function renderExploreCard(raw: Record<string, unknown>): TemplateResult {
  const path = typeof raw.path === 'string' && raw.path.startsWith('/explore/') ? raw.path : null;
  const title = typeof raw.title === 'string' ? raw.title : 'In Explore';
  return html`
    <div class="assistant-plotcard argus-assist-card">
      <div class="assistant-plotcard-head">
        <span class="assistant-plotcard-title">${title}</span>
        ${path
          ? html`<a class="assistant-send" href=${path} title="The same series, window and entity in Explore">Open in Explore →</a>`
          : nothing}
      </div>
      <span class="assistant-plotcard-type">chart · the series the rule reads</span>
    </div>
  `;
}

registerAssistantCard(ARGUS_EXPLORE_CARD, renderExploreCard);

/* ── the charts a rule's alerts show ─────────────────────── */

export const ARGUS_CHARTS_CARD = 'argus.charts_edit';

interface ChartsPayload {
  rule_id?: string;
  name?: string;
  charts?: { title: string; query?: string; plot?: PlotSpec }[];
  summary?: string;
}

/** The list to store: kept charts as they are, new ones as recipes without ship or time. */
export function chartsFromCard(charts: ChartsPayload['charts']): ArgusRuleChart[] {
  return (charts ?? []).map((c) => ({ title: c.title, query: c.query ?? stripContext(specToQuery(c.plot!)) }));
}

// Like the Charts zone: stored at once, through the same route, as this person.
async function arriveCharts(raw: Record<string, unknown>): Promise<void> {
  const payload = raw as ChartsPayload;
  if (!payload.rule_id) return;
  let failed: string | null;
  try {
    failed = await setCharts(payload.rule_id, chartsFromCard(payload.charts));
  } catch {
    failed = 'SONAR wrote a chart this page cannot read. Nothing was changed.';
  }
  if (failed) showToast('error', failed);
}

function renderChartsCard(raw: Record<string, unknown>): TemplateResult {
  const payload = raw as ChartsPayload;
  const charts = payload.charts ?? [];
  return html`
    <div class="assistant-plotcard argus-assist-card">
      <div class="assistant-plotcard-head">
        <span class="assistant-plotcard-title">Charts · ${payload.name ?? ''}</span>
      </div>
      <span class="assistant-plotcard-type">applied at once · no Save needed</span>
      ${payload.summary ? html`<p>${payload.summary}</p>` : nothing}
      <ul class="assistant-dashcard-list">
        ${charts.map((c) => html`<li>${c.title}</li>`)}
        <li>Argus's guess</li>
      </ul>
    </div>
  `;
}

registerAssistantCard(ARGUS_CHARTS_CARD, renderChartsCard, (raw) => void arriveCharts(raw));
